<script setup lang="ts">
  import { useTimeoutFn } from '@vueuse/core';
  import { computed, ref, watch } from 'vue';

  import type { Task, TaskRequirement, TaskRequirementOption } from '@stuut/api/models';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import TaskRequirementInput from './TaskRequirementInput.vue';

  const props = defineProps<{
    requirement: TaskRequirement;
    task: Task;
    partnerId?: string;
  }>();

  const emit = defineEmits<{
    (e: 'approve'): void;
    (e: 'reject'): void;
    (e: 'dismiss'): void;
    (e: 'resolve', value: string): void;
    (e: 'selectOption', option: TaskRequirementOption): void;
  }>();

  const isDisabled = computed(() => props.requirement.isDisabled);

  const resolutionLabel = computed(() => {
    if (props.requirement.isDismissed) return 'Dismissed';

    const resolution = props.requirement.resolution;
    const displayName = resolution?.displayName ?? resolution?.display_name;
    if (displayName) return displayName;

    const value = props.requirement.resolution?.value;

    if (typeof value === 'boolean') return value ? 'Approved' : 'Rejected';
    if (props.requirement.resolution?.type === 'key') {
      const selectedOption = props.requirement.options?.find((option) => option.key === value);
      if (selectedOption?.label) return selectedOption.label;
    }

    return value;
  });

  type SaveState = 'idle' | 'saving' | 'saved';
  const saveState = ref<SaveState>('idle');
  const isSaveBlocking = computed(() => saveState.value !== 'idle');

  const MIN_SAVING_MS = 1000;
  const SAVED_MS = 1000;
  const SAVING_TIMEOUT_MS = 10_000;

  let savingStartedAt = 0;
  const minSavingDelay = ref(0);

  const { start: scheduleSafety, stop: stopSafety } = useTimeoutFn(
    () => {
      if (saveState.value === 'saving') saveState.value = 'idle';
    },
    SAVING_TIMEOUT_MS,
    { immediate: false },
  );

  const { start: scheduleSavedClear, stop: stopSavedClear } = useTimeoutFn(
    () => {
      saveState.value = 'idle';
    },
    SAVED_MS,
    { immediate: false },
  );

  const finishSave = () => {
    if (props.requirement.isAnswered) {
      saveState.value = 'saved';
      scheduleSavedClear();
    } else {
      saveState.value = 'idle';
    }
  };

  const { start: scheduleMinSaving, stop: stopMinSaving } = useTimeoutFn(
    finishSave,
    minSavingDelay,
    { immediate: false },
  );

  const beginSave = () => {
    stopSavedClear();
    stopMinSaving();
    saveState.value = 'saving';
    savingStartedAt = Date.now();
    scheduleSafety();
  };

  const onResolve = (val: string) => {
    beginSave();
    emit('resolve', val);
  };

  const onSelectOption = (opt: TaskRequirementOption) => {
    beginSave();
    emit('selectOption', opt);
  };

  const onApprove = () => {
    beginSave();
    emit('approve');
  };

  const onReject = () => {
    beginSave();
    emit('reject');
  };

  const onDismiss = () => {
    beginSave();
    emit('dismiss');
  };

  watch(
    () => props.requirement.modified_at,
    () => {
      if (saveState.value !== 'saving') return;
      stopSafety();
      const remaining = Math.max(0, MIN_SAVING_MS - (Date.now() - savingStartedAt));
      if (remaining === 0) {
        finishSave();
      } else {
        minSavingDelay.value = remaining;
        scheduleMinSaving();
      }
    },
  );
</script>

<template>
  <div class="flex flex-col gap-y-3 text-xs" :class="isDisabled && 'opacity-30'">
    <div class="font-semibold flex items-start gap-1.5">
      <div class="size-4 flex items-center justify-start">
        <Transition
          mode="out-in"
          enter-active-class="transition-opacity duration-200"
          leave-active-class="transition-opacity duration-200"
          enter-from-class="opacity-0"
          leave-to-class="opacity-0"
        >
          <span v-if="isSaveBlocking" key="active" class="flex items-center justify-center">
            <LoadingSpinner v-if="saveState === 'saving'" :size="12" />
            <i v-else class="fas fa-floppy-disk text-muted-foreground" />
          </span>
          <i
            v-else
            key="idle"
            :class="{
              'fas fa-question-circle text-warning': requirement?.isOpen || requirement?.isSkipped,
              'fas fa-check text-success': requirement?.isAnswered,
              'fas fa-check-circle text-success': requirement?.isCompleted,
              'far fa-circle-xmark text-destructive': requirement?.isDismissed,
            }"
          />
        </Transition>
      </div>

      <span>{{ requirement.prompt }}</span>

      <Tooltip v-if="requirement.context">
        <TooltipTrigger>
          <i class="far fa-circle-info text-muted-foreground" />
        </TooltipTrigger>

        <TooltipContent>
          {{ requirement.context }}
        </TooltipContent>
      </Tooltip>
    </div>

    <div v-if="!isDisabled" class="flex items-end gap-x-4 w-full flex-wrap">
      <TaskRequirementInput
        :partner-id="partnerId"
        :requirement="requirement"
        :saving="isSaveBlocking"
        :is-task-open="!!task?.isOpen"
        @approve="onApprove"
        @reject="onReject"
        @dismiss="onDismiss"
        @resolve="onResolve"
        @select-option="onSelectOption"
      />
    </div>

    <em v-else class="text-muted-foreground first-letter:uppercase ml-5">
      {{ resolutionLabel }}
    </em>
  </div>
</template>
